// client/src/pages/Pricing.js
// Public wholesaler pricing page — visual style mirrors WholesalerPricing.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import pricingContent from '../data/pricingContent';

const TIER_COLORS = {
  blue: {
    border: 'border-blue-200',
    headerBg: 'bg-blue-50',
    accentText: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-700'
  },
  purple: {
    border: 'border-purple-200',
    headerBg: 'bg-purple-50',
    accentText: 'text-purple-600',
    button: 'bg-purple-600 hover:bg-purple-700'
  },
  amber: {
    border: 'border-amber-200',
    headerBg: 'bg-amber-50',
    accentText: 'text-amber-600',
    button: 'bg-amber-600 hover:bg-amber-700'
  }
};

const Pricing = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';
  const content = pricingContent[currentLanguage] || pricingContent.en;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {content.heroTitle}
          </h1>
          <p className="text-lg text-gray-600 mb-1">
            {content.heroSubtitle}
          </p>
          <p className="text-sm text-gray-500">
            {content.compareIntro}
          </p>
        </div>

        {/* Business free callout */}
        <div className="max-w-3xl mx-auto mb-8 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <h2 className="text-base font-semibold text-green-900 mb-1">
            {content.businessCalloutTitle}
          </h2>
          <p className="text-sm text-green-800">
            {content.businessCalloutBody}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12 mt-10">
          {content.tiers.map((tier) => {
            const colors = TIER_COLORS[tier.color] || TIER_COLORS.blue;
            const isPopular = Boolean(tier.popular);
            const isContact = Boolean(tier.contact);

            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-lg border-2 ${colors.border} flex flex-col ${
                  isPopular ? 'shadow-2xl md:scale-105' : 'shadow-lg'
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      ⭐ {content.popularBadge}
                    </span>
                  </div>
                )}

                {/* Contact Badge */}
                {isContact && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      {content.contactBadge}
                    </span>
                  </div>
                )}

                {/* Header */}
                <div className={`${colors.headerBg} px-6 py-6 rounded-t-lg ${isPopular || isContact ? 'pt-8' : 'pt-6'}`}>
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-800 mb-1">
                      {tier.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 min-h-[40px]">
                      {tier.tagline}
                    </p>
                    <div className="mb-2">
                      {isContact || tier.price == null ? (
                        <span className="text-3xl font-bold text-gray-900">
                          {content.contactPrice}
                        </span>
                      ) : (
                        <>
                          <span className="text-5xl font-bold text-gray-900">€{tier.price}</span>
                          <span className="text-gray-600">{content.monthSuffix}</span>
                        </>
                      )}
                    </div>
                    {tier.target && (
                      <p className="text-xs text-gray-500 italic">{tier.target}</p>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-6 flex flex-col flex-1">
                  {/* Product Limit + Per-order */}
                  <div className="text-center mb-4 pb-4 border-b border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {tier.productLimit === 'unlimited' ? '∞' : tier.productLimit}
                    </div>
                    <div className="text-sm text-gray-600">
                      {tier.productLimit === 'unlimited'
                        ? content.unlimitedLabel + ' ' + content.productsLabel
                        : content.productsLabel}
                    </div>
                    <div className={`mt-1 text-xs font-semibold ${colors.accentText}`}>
                      €{tier.perOrderFee.toFixed(2)} {content.perOrderShort}
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-6 flex-1">
                    {tier.highlights.map((feature, index) => (
                      <li key={index} className="flex items-start text-xs text-gray-700">
                        <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {tier.ctaHref.startsWith('mailto:') ? (
                    <a
                      href={tier.ctaHref}
                      className={`w-full ${colors.button} text-white px-6 py-2.5 rounded-lg font-semibold transition-colors text-sm text-center block`}
                    >
                      {content[tier.cta]}
                    </a>
                  ) : (
                    <Link
                      to={tier.ctaHref}
                      className={`w-full ${colors.button} text-white px-6 py-2.5 rounded-lg font-semibold transition-colors text-sm text-center block`}
                    >
                      {content[tier.cta]}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden mb-12 max-w-5xl mx-auto">
          <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <h2 className="text-2xl font-bold text-gray-800 mb-1">
              {content.compareTitle}
            </h2>
            <p className="text-sm text-gray-600">{content.compareIntro}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold text-gray-700 w-1/3"> </th>
                  {content.tiers.map((tier) => {
                    const colors = TIER_COLORS[tier.color] || TIER_COLORS.blue;
                    return (
                      <th
                        key={tier.id}
                        className={`text-center px-4 py-3 font-semibold ${colors.accentText}`}
                      >
                        {tier.name}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {content.comparison.map((group) => (
                  <React.Fragment key={group.group}>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td
                        colSpan={content.tiers.length + 1}
                        className="px-6 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {group.group}
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.label} className="border-t border-gray-100">
                        <td className="px-6 py-3 text-gray-700">{row.label}</td>
                        {row.values.map((value, idx) => (
                          <td
                            key={idx}
                            className="text-center px-4 py-3 text-gray-800"
                          >
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Contact CTA — gradient */}
        <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold mb-3">
            {currentLanguage === 'el'
              ? 'Έτοιμοι να ξεκινήσετε;'
              : 'Ready to get started?'}
          </h2>
          <p className="text-base mb-6 opacity-95">
            {currentLanguage === 'el'
              ? 'Δημιουργήστε λογαριασμό χονδρεμπόρου σε λίγα λεπτά ή επικοινωνήστε με την ομάδα μας.'
              : 'Create a wholesaler account in minutes, or talk to our team.'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/wholesaler/register"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {content.ctaRegister}
            </Link>
            <a
              href={`mailto:${content.contactEmail}`}
              className="bg-white/10 text-white border border-white/40 px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors"
            >
              📧 {content.contactEmail}
            </a>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/faq" className="text-white/90 hover:text-white underline">
              {content.faqLinkText}
            </Link>
            <Link to="/help" className="text-white/90 hover:text-white underline">
              {content.helpLinkText}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
